import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { useAuthStore } from '../store/authStore';
import { FooterSection } from '../components/landing';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AiReport {
  executive_summary: string;
  key_themes?: string[];
  action_items?: string[];
  sentiment_score?: number;
  sentiment_label?: string;
  strategic_insight?: string;
}

type PageState = 'idle' | 'loading' | 'preview' | 'full';
type AuthTab = 'register' | 'login';

interface RetroTemplate {
  value: string;
  label: string;
  columns: string[];
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// ─── Template definitions ─────────────────────────────────────────────────────

const TEMPLATES: RetroTemplate[] = [
  {
    value: 'Went Well / To Improve / Action Items',
    label: 'Went Well / To Improve',
    columns: ['What went well?', 'What to improve?'],
  },
  {
    value: 'Start / Stop / Continue',
    label: 'Start / Stop / Continue',
    columns: ['Start', 'Stop', 'Continue'],
  },
  {
    value: 'Mad / Sad / Glad',
    label: 'Mad / Sad / Glad',
    columns: ['Mad', 'Sad', 'Glad'],
  },
  {
    value: '4Ls (Liked / Learned / Lacked / Longed For)',
    label: '4Ls',
    columns: ['Liked', 'Learned', 'Lacked', 'Longed For'],
  },
  {
    value: 'Sailboat',
    label: 'Sailboat',
    columns: ['Wind (what pushed us forward)', 'Anchor (what held us back)', 'Island (our goal)', 'Rocks (risks)'],
  },
];

const COLUMN_PLACEHOLDERS: Record<string, string> = {
  'What went well?': 'e.g. The new deployment pipeline is 5x faster — zero rollbacks this sprint. Team energy was high after the demo.',
  'What to improve?': 'e.g. Daily stand-ups are taking too long. Unclear acceptance criteria caused 2 re-works. Code review bottleneck on Fridays.',
  'Start': 'e.g. Pair programming on complex features, 15-min async video updates instead of long meetings...',
  'Stop': 'e.g. Skipping retros when under pressure, adding unplanned work mid-sprint without reprioritizing...',
  'Continue': 'e.g. Weekly end-of-sprint demos to stakeholders, dedicated QA day before release...',
  'Mad': 'e.g. Unclear requirements that changed 3 times, blocked waiting on external API access for 4 days...',
  'Sad': 'e.g. Missed sprint goal by one story point, a key team member is leaving next month...',
  'Glad': 'e.g. Successful production launch with zero incidents, customer NPS jumped 12 points...',
  'Liked': 'e.g. Team was highly engaged and collaborative, we hit our velocity target for the 3rd sprint in a row...',
  'Learned': 'e.g. Feature flags make progressive rollouts much safer, mob programming sessions boosted knowledge sharing...',
  'Lacked': 'e.g. A clear definition of done for UX tasks, design specs delivered before sprint start...',
  'Longed For': 'e.g. More automation in our CI pipeline, a dedicated QA resource for exploratory testing...',
  'Wind (what pushed us forward)': 'e.g. Strong stakeholder support, team highly motivated after last sprint\'s success...',
  'Anchor (what held us back)': 'e.g. Growing technical debt in the auth service, unexpected dependency on another team\'s release...',
  'Island (our goal)': 'e.g. Ship v2.0 checkout flow and reduce cart abandonment by 10%...',
  'Rocks (risks)': 'e.g. Third-party API reliability, key engineer availability during holidays...',
};

// ─── Column theme helpers ──────────────────────────────────────────────────────

type ColumnTheme = 'green' | 'red' | 'violet' | 'amber' | 'default';

function getColumnTheme(col: string): ColumnTheme {
  const lower = col.toLowerCase();
  if (lower.includes('went well') || lower.includes('liked') || lower.includes('glad') || lower.includes('continue') || lower.includes('wind')) return 'green';
  if (lower.includes('improve') || lower.includes('mad') || lower.includes('sad') || lower.includes('stop') || lower.includes('anchor') || lower.includes('lacked') || lower.includes('rocks')) return 'red';
  if (lower.includes('start') || lower.includes('learned') || lower.includes('island') || lower.includes('longed')) return 'violet';
  return 'default';
}

const COLUMN_THEME_STYLES: Record<ColumnTheme, { border: string; bg: string; labelCls: string; iconEl: React.ReactNode }> = {
  green: {
    border: 'border-emerald-200',
    bg: 'bg-emerald-50/40',
    labelCls: 'text-emerald-700',
    iconEl: (
      <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  red: {
    border: 'border-red-200',
    bg: 'bg-red-50/40',
    labelCls: 'text-red-700',
    iconEl: (
      <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
    ),
  },
  violet: {
    border: 'border-orange-200',
    bg: 'bg-orange-50/40',
    labelCls: 'text-orange-700',
    iconEl: (
      <svg className="w-4 h-4 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  amber: {
    border: 'border-amber-200',
    bg: 'bg-amber-50/40',
    labelCls: 'text-amber-700',
    iconEl: (
      <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.347.347a3.5 3.5 0 01-4.95 0l-.347-.347z" />
      </svg>
    ),
  },
  default: {
    border: 'border-slate-200',
    bg: 'bg-white',
    labelCls: 'text-slate-700',
    iconEl: <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0 mt-1" />,
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getPreviewText(text: string): string {
  const sentences = text.match(/[^.!?]+[.!?]+/g) ?? [];
  return sentences.slice(0, 2).join(' ').trim() || text.slice(0, 200);
}

function sentimentLabel(score: number): { label: string; color: string } {
  if (score >= 0.7) return { label: 'Positive', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
  if (score >= 0.4) return { label: 'Neutral', color: 'text-amber-600 bg-amber-50 border-amber-200' };
  return { label: 'Needs Attention', color: 'text-red-600 bg-red-50 border-red-200' };
}

function todayISODate(): string {
  return new Date().toISOString().split('T')[0];
}

function buildSampleData(template: RetroTemplate) {
  const sprintName = 'Sprint 42 — Platform Team';
  const retroDate = todayISODate();

  const byTemplate: Record<string, Record<string, string>> = {
    'Went Well / To Improve / Action Items': {
      'What went well?': [
        '- On-call rotation improvements reduced mean time to acknowledge from ~12m to ~4m.',
        '- Feature flags helped us ship the new checkout safely with a 10% gradual rollout.',
        '- Pairing on the caching layer prevented a costly performance regression.',
      ].join('\n'),
      'What to improve?': [
        '- Two deployments failed due to missing env vars; the change wasn’t reflected in the release checklist.',
        '- Incident comms were fragmented across Slack threads; decisions weren’t captured anywhere.',
        '- PR review queue spiked on Thursdays/Fridays causing last-minute merges and hotfix risk.',
      ].join('\n'),
    },
    'Start / Stop / Continue': {
      'Start': [
        '- A 10-minute pre-release “change review” to confirm flags, env vars, and rollback steps.',
        '- A single incident channel + a lightweight timeline doc during outages.',
        '- Rotating “review captain” to keep PRs moving (especially end of sprint).',
      ].join('\n'),
      'Stop': [
        '- Merging large PRs late Friday without a clear rollback plan.',
        '- “Drive-by” scope changes mid-sprint without reprioritization and owner alignment.',
        '- Relying on tribal knowledge for deployment steps.',
      ].join('\n'),
      'Continue': [
        '- Shipping behind feature flags and doing progressive rollouts.',
        '- Weekly demo with stakeholders — it’s catching misunderstandings early.',
        '- Post-incident review within 48 hours while context is fresh.',
      ].join('\n'),
    },
    'Mad / Sad / Glad': {
      'Mad': [
        '- CI was flaky (intermittent E2E failures), blocking merges for hours.',
        '- Unclear ownership on a dependency upgrade delayed the release.',
      ].join('\n'),
      'Sad': [
        '- We spent too much time firefighting and didn’t move the roadmap forward.',
        '- A few teammates felt out of the loop during the incident.',
      ].join('\n'),
      'Glad': [
        '- Customer-reported bugs dropped after we added synthetic monitoring.',
        '- The team collaborated calmly during the outage and resolved it in under 30 minutes.',
      ].join('\n'),
    },
    '4Ls (Liked / Learned / Lacked / Longed For)': {
      'Liked': [
        '- Clear sprint goal and less context switching than last sprint.',
        '- The new “release captain” experiment improved deploy confidence.',
      ].join('\n'),
      'Learned': [
        '- Missing environment variables should be validated at build/release time.',
        '- Smaller PRs get reviewed faster and reduce late-sprint risk.',
      ].join('\n'),
      'Lacked': [
        '- A shared place to capture decisions during incidents.',
        '- Consistent definition of done for “deployment readiness”.',
      ].join('\n'),
      'Longed For': [
        '- Automated checks for required env vars and migrations before deploy.',
        '- A simple weekly “alignment note” to reduce miscommunication across squads.',
      ].join('\n'),
    },
    'Sailboat': {
      'Wind (what pushed us forward)': [
        '- Strong collaboration between backend and frontend during the checkout rollout.',
        '- Feature flags + observability made releases safer.',
      ].join('\n'),
      'Anchor (what held us back)': [
        '- Deployment checklist wasn’t updated; two releases failed due to configuration drift.',
        '- PR reviews bottlenecked, leaving less time for validation.',
      ].join('\n'),
      'Island (our goal)': [
        '- Stabilize deployments and reduce failed releases by 80% next sprint.',
        '- Improve incident communication so every stakeholder has a single source of truth.',
      ].join('\n'),
      'Rocks (risks)': [
        '- Another dependency upgrade could break CI again.',
        '- End-of-sprint merges increase rollback risk if reviews lag.',
      ].join('\n'),
    },
  };

  const columns = byTemplate[template.value] || byTemplate['Start / Stop / Continue'];
  const actionItemsText = [
    'Add an env-var validation step to the release pipeline (fail fast).',
    'Introduce a single incident channel + timeline doc template (owner: on-call lead).',
    'Trial “review captain” rotation for 2 sprints and measure PR cycle time.',
  ].join('\n');

  return { sprintName, retroDate, columns, actionItemsText };
}

// ─── LoadingCard ──────────────────────────────────────────────────────────────

const LoadingCard: React.FC = () => (
  <div className="w-full max-w-2xl mx-auto mt-8">
    <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 flex flex-col items-center gap-6">
      <div className="relative">
        <div className="w-16 h-16 rounded-full border-4 border-orange-100 border-t-orange-500 animate-spin" />
        <div className="absolute inset-0 flex items-center justify-center">
          <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15M14.25 3.104c.251.023.501.05.75.082M19.8 15l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.607L5 14.5m14.8.5l-1.57.393" />
          </svg>
        </div>
      </div>
      <div className="text-center">
        <p className="text-lg font-semibold text-slate-800">AI is analyzing your sprint...</p>
        <p className="text-sm text-slate-500 mt-1">This usually takes 15–30 seconds</p>
      </div>
      <div className="w-full space-y-2">
        {['Identifying key themes...', 'Extracting action items...', 'Generating executive summary...'].map((step) => (
          <div key={step} className="flex items-center gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
            <span className="text-sm text-slate-500 animate-pulse">{step}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── AuthModal ────────────────────────────────────────────────────────────────

const AuthModal: React.FC<{ onSuccess: () => void; onClose: () => void }> = ({ onSuccess, onClose }) => {
  const [tab, setTab] = useState<AuthTab>('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [localError, setLocalError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { register, login, error: authError, clearError } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();
    if (tab === 'register' && password !== passwordConfirm) { setLocalError('Passwords do not match.'); return; }
    if (password.length < 8) { setLocalError('Password must be at least 8 characters.'); return; }
    setSubmitting(true);
    try {
      if (tab === 'register') await register({ email, password, password_confirm: passwordConfirm });
      else await login({ email, password });
      onSuccess();
    } catch { /* authError set by store */ } finally { setSubmitting(false); }
  };

  const displayError = localError || authError;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Unlock Your Full Report</h2>
            <p className="text-orange-100 text-sm mt-0.5">Free account — no credit card needed</p>
          </div>
          <button onClick={onClose} className="text-orange-100 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex border-b border-slate-100">
          {(['register', 'login'] as AuthTab[]).map((t) => (
            <button key={t} onClick={() => { setTab(t); setLocalError(''); clearError(); }}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${tab === t ? 'text-orange-600 border-b-2 border-orange-500' : 'text-slate-500 hover:text-slate-700'}`}>
              {t === 'register' ? 'Create Account' : 'Sign In'}
            </button>
          ))}
        </div>
        <div className="px-6 pt-5 pb-3">
          <Link to="/register" className="flex items-center justify-center gap-2 w-full py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
             Sign up with Google
          </Link>
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-slate-100" /><span className="text-xs text-slate-400">or</span><div className="flex-1 h-px bg-slate-100" />
          </div>
        </div>
        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-3">
          {displayError && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{displayError}</div>}
          <input type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
          <input type="password" placeholder="Password (min 8 characters)" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
          {tab === 'register' && <input type="password" placeholder="Confirm password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} required className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent" />}
          <button type="submit" disabled={submitting} className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white rounded-lg text-sm font-semibold transition-colors">
            {submitting ? 'Please wait...' : tab === 'register' ? 'Create Free Account & Unlock Report' : 'Sign In & Unlock Report'}
          </button>
          {tab === 'register' && (
            <p className="text-xs text-center text-slate-400">
              By creating an account you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

// ─── ReportSection ────────────────────────────────────────────────────────────

const ReportSection: React.FC<{
  report: AiReport;
  isFullView: boolean;
  onUnlockClick: () => void;
  reportRef: React.RefObject<HTMLDivElement | null>;
}> = ({ report, isFullView, onUnlockClick, reportRef }) => {
  const previewText = getPreviewText(report.executive_summary);
  const sentiment = report.sentiment_score != null
    ? { ...sentimentLabel(report.sentiment_score), label: report.sentiment_label || sentimentLabel(report.sentiment_score).label }
    : null;

  return (
    <div ref={reportRef} className="w-full max-w-3xl mx-auto mt-8 space-y-4 print:mt-0">
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white print:rounded-none">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium bg-white/20 px-2 py-0.5 rounded-full uppercase tracking-wide">AI Report</span>
              {sentiment && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${sentiment.color}`}>
                  {sentiment.label}{report.sentiment_score != null && <span className="ml-1 opacity-70">({Math.round(report.sentiment_score * 100)}%)</span>}
                </span>
              )}
            </div>
            <h2 className="text-xl font-bold">Sprint Retrospective Report</h2>
          </div>
          <span className="text-orange-100 text-sm">{new Date().toLocaleDateString()}</span>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Executive Summary</h3>
        </div>
        <div className="px-6 py-5 relative">
          {isFullView ? (
            <p className="text-slate-700 leading-relaxed">{report.executive_summary}</p>
          ) : (
            <>
              <p className="text-slate-700 leading-relaxed">{previewText}</p>
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent" />
            </>
          )}
        </div>
      </div>

      {!isFullView && (
        <div className="bg-white rounded-2xl border border-orange-200 shadow-sm p-8 text-center">
          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-2">Your full report is ready</h3>
          <p className="text-slate-500 text-sm mb-5 max-w-sm mx-auto">Create a free account to unlock Key Themes, Action Items, and download your report as PDF.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={onUnlockClick} className="px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold text-sm transition-colors shadow-lg shadow-orange-500/25">Unlock Full Report — Free</button>
            <button onClick={onUnlockClick} className="px-6 py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl font-semibold text-sm transition-colors">Sign In</button>
          </div>
          <p className="text-xs text-slate-400 mt-3">No credit card • Takes 30 seconds</p>
        </div>
      )}

      {isFullView && (
        <>
          {report.key_themes && report.key_themes.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Key Themes</h3>
              </div>
              <ul className="px-6 py-5 space-y-2">
                {report.key_themes.map((theme, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-slate-700 text-sm leading-relaxed">
                    <span className="mt-1 w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs flex items-center justify-center font-semibold flex-shrink-0">{i + 1}</span>
                    {theme}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.action_items && report.action_items.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Action Items</h3>
                <span className="ml-auto text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{report.action_items.length} items</span>
              </div>
              <ul className="px-6 py-5 space-y-2.5">
                {report.action_items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-slate-700 text-sm leading-relaxed">
                    <svg className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.strategic_insight && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Strategic Insight</h3>
              </div>
              <div className="px-6 py-5">
                <p className="text-slate-700 leading-relaxed text-sm">{report.strategic_insight}</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const FreeAiReportPage: React.FC = () => {
  useSEO({
    title: 'Elevate Your Team’s Velocity — Instant Retro Insights',
    description: 'Turn messy retro notes into actionable insights in seconds: Executive Summary, Key Themes, Action Items, and sentiment — instantly. Free to try.',
    canonical: '/tools/ai-retrospective-report-generator',
    ogType: 'website',
  });

  // ── Form state ──
  const [sprintName, setSprintName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<RetroTemplate>(TEMPLATES[0]);
  const [retroDate, setRetroDate] = useState(todayISODate());
  const [columnValues, setColumnValues] = useState<Record<string, string>>({});
  const [actionItemsText, setActionItemsText] = useState('');

  // ── Page state ──
  const [pageState, setPageState] = useState<PageState>('idle');
  const [report, setReport] = useState<AiReport | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const reportRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, tokens } = useAuthStore();

  const handleTemplateChange = (tpl: RetroTemplate) => {
    setSelectedTemplate(tpl);
    setColumnValues({});
  };

  const setColumnValue = (col: string, val: string) => {
    setColumnValues((prev) => ({ ...prev, [col]: val }));
  };

  const handleTrySampleData = () => {
    if (pageState === 'loading') return;
    const tpl = TEMPLATES.find((t) => t.value === 'Start / Stop / Continue') || TEMPLATES[0];
    const sample = buildSampleData(tpl);
    setSelectedTemplate(tpl);
    setSprintName(sample.sprintName);
    setRetroDate(sample.retroDate);
    setColumnValues(sample.columns);
    setActionItemsText(sample.actionItemsText);
    setErrorMsg(null);
  };

  const totalChars = Object.values(columnValues).join('').length + actionItemsText.length;
  const isFormValid = sprintName.trim().length > 0 && totalChars >= 30;

  const handleGenerate = async () => {
    if (!isFormValid) return;
    setErrorMsg(null);
    setPageState('loading');

    // Build cards string: label: content pairs for each non-empty column
    const cardsArr = selectedTemplate.columns
      .filter((col) => (columnValues[col] || '').trim())
      .map((col) => `${col}: ${columnValues[col].trim()}`);

    const payload = {
      sprint_name: sprintName.trim(),
      template: selectedTemplate.value,
      date: retroDate,
      cards: cardsArr.join(' | '),
      action_items: actionItemsText.trim(),
    };

    try {
      const res = await fetch(`${API_BASE}/public/ai-report/generate/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        setErrorMsg(res.status === 429
          ? (data.message || 'Daily limit reached. Create a free account for more reports.')
          : (data.message || 'Something went wrong. Please try again.'));
        setPageState('idle');
        return;
      }

      setReport(data.report);
      setSessionToken(data.session_token);

      if (isAuthenticated) {
        setPageState('full');
        claimReport(data.session_token);
      } else {
        setPageState('preview');
        setTimeout(() => reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
      setPageState('idle');
    }
  };

  const claimReport = async (token: string) => {
    const accessToken = tokens?.access;
    if (!accessToken) return;
    try {
      await fetch(`${API_BASE}/public/ai-report/claim/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ session_token: token }),
      });
    } catch { /* non-critical */ }
  };

  const handleAuthSuccess = async () => {
    setShowAuthModal(false);
    setPageState('full');
    if (sessionToken) {
      setTimeout(() => {
        const { tokens: t } = useAuthStore.getState();
        if (t?.access && sessionToken) {
          fetch(`${API_BASE}/public/ai-report/claim/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t.access}` },
            body: JSON.stringify({ session_token: sessionToken }),
          }).catch(() => {});
        }
      }, 500);
    }
    setTimeout(() => reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleReset = () => {
    setPageState('idle');
    setReport(null);
    setSessionToken(null);
    setErrorMsg(null);
  };

  const isFullView = pageState === 'full';

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`@media print { .no-print { display: none !important; } }`}</style>

      <div className="max-w-6xl mx-auto px-4 pt-7 pb-16">

        {/* ── Hero Header ── */}
        {(pageState === 'idle' || pageState === 'loading') && (
          <div className="no-print mb-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5 bg-orange-100 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                ✨ Free Tool — No Sign-up Required
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleTrySampleData}
                  disabled={pageState === 'loading'}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span aria-hidden>✨</span>
                  Try with Sample Data
                </button>
              </div>
            </div>

            <div className="mt-4">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight">
                Elevate Your Team’s Velocity
              </h1>
              <p className="text-slate-500 text-base sm:text-lg mt-2 max-w-2xl">
                Turn messy retro notes into actionable insights in seconds.
              </p>
            </div>
          </div>
        )}

        {/* ── Form ── */}
        {(pageState === 'idle' || pageState === 'loading') && (
          <div className="no-print grid lg:grid-cols-5 gap-4 items-start">
            <div className="lg:col-span-3 space-y-4">

            {/* Step 1 — Sprint info */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-6">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-600 text-xs flex items-center justify-center font-bold">1</span>
                Sprint Info
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Sprint / Team Name <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    value={sprintName}
                    onChange={(e) => setSprintName(e.target.value)}
                    placeholder="e.g. Sprint 42 — Platform Team"
                    disabled={pageState === 'loading'}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent disabled:bg-slate-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Retro Date</label>
                  <input
                    type="date"
                    value={retroDate}
                    onChange={(e) => setRetroDate(e.target.value)}
                    disabled={pageState === 'loading'}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent disabled:bg-slate-50"
                  />
                </div>
              </div>
            </div>

            {/* Step 2 — Template */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-6">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-600 text-xs flex items-center justify-center font-bold">2</span>
                Retro Format
              </h2>
              <div className="flex flex-wrap gap-2">
                {TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.value}
                    onClick={() => handleTemplateChange(tpl)}
                    disabled={pageState === 'loading'}
                    className={`px-3.5 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      selectedTemplate.value === tpl.value
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : 'border-slate-200 text-slate-600 hover:border-orange-300 hover:text-orange-600'
                    }`}
                  >
                    {tpl.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 3 — Column inputs (individual colored cards) */}
            <div className="space-y-3">
              {selectedTemplate.columns.map((col) => {
                const theme = getColumnTheme(col);
                const { border, bg, labelCls, iconEl } = COLUMN_THEME_STYLES[theme];
                return (
                  <div key={col} className={`rounded-2xl border shadow-sm p-5 ${border} ${bg}`}>
                    <label className="flex items-center gap-2 text-xs font-semibold mb-2.5 uppercase tracking-wide">
                      {iconEl}
                      <span className={labelCls}>{col}</span>
                    </label>
                    <textarea
                      value={columnValues[col] || ''}
                      onChange={(e) => setColumnValue(col, e.target.value)}
                      placeholder={COLUMN_PLACEHOLDERS[col] || `Enter notes for ${col}...`}
                      rows={3}
                      disabled={pageState === 'loading'}
                      className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent disabled:bg-slate-50 leading-relaxed"
                    />
                  </div>
                );
              })}
            </div>

            {/* Step 4 — Action items */}
            <div className="rounded-2xl border shadow-sm p-5 border-orange-200 bg-orange-50/40">
              <label className="flex items-center gap-2 text-xs font-semibold mb-2.5 uppercase tracking-wide">
                <svg className="w-4 h-4 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <span className="text-orange-700">Action Items</span>
                <span className="text-slate-400 font-normal normal-case text-xs ml-1">(optional)</span>
              </label>
              <textarea
                value={actionItemsText}
                onChange={(e) => setActionItemsText(e.target.value)}
                placeholder="e.g. Set scope freeze 3 days before sprint end, introduce dedicated QA day, run weekly team health checks..."
                rows={3}
                disabled={pageState === 'loading'}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent disabled:bg-slate-50 leading-relaxed"
              />
              <p className="text-xs text-slate-400 mt-1.5">Separate multiple items with commas or new lines.</p>
            </div>

            {/* Error */}
            {errorMsg && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-start gap-2">
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {errorMsg}
              </div>
            )}

            {/* Generate button */}
            <button
              onClick={handleGenerate}
              disabled={pageState === 'loading' || !isFormValid}
              className="w-full py-4 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-2xl font-extrabold text-lg transition-all shadow-xl shadow-slate-900/20 hover:shadow-slate-900/30 hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              {pageState === 'loading' ? (
                <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Generating report...</>
              ) : (
                <><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.347.347a3.5 3.5 0 01-4.95 0l-.347-.347z" />
                </svg>Generate My Professional Report — Free</>
              )}
            </button>

            {/* Micro-copy */}
            <p className="text-center text-sm text-slate-400">
              No credit card, no initial signup, just insights.
            </p>

            {!isFormValid && sprintName.trim().length === 0 && (
              <p className="text-center text-xs text-slate-400">Fill in the sprint name and at least one retro column to generate.</p>
            )}

            {/* How it works */}
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-sm px-6 py-5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide text-center mb-4">How it works</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-0">
                {[
                  { step: '1', label: 'Paste Notes', icon: '📋' },
                  { step: '2', label: 'AI Analyzes', icon: '🤖' },
                  { step: '3', label: 'Get Action Items', icon: '✅' },
                ].map(({ step, label, icon }, i) => (
                  <React.Fragment key={step}>
                    <div className="flex flex-col items-center gap-1.5 text-center flex-1">
                      <span className="text-2xl">{icon}</span>
                      <div className="flex items-center gap-1.5">
                        <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-600 text-xs flex items-center justify-center font-bold flex-shrink-0">{step}</span>
                        <span className="text-sm font-medium text-slate-700">{label}</span>
                      </div>
                    </div>
                    {i < 2 && (
                      <div className="hidden sm:block text-slate-300 px-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-xs text-slate-500 pt-2">
              {[{ icon: '🔒', text: 'Not stored beyond 7 days' }, { icon: '⚡', text: '~20 seconds' }, { icon: '🆓', text: '3 free/day' }, { icon: '📄', text: 'Download PDF' }].map(({ icon, text }) => (
                <div key={text} className="flex items-center gap-1.5"><span>{icon}</span><span>{text}</span></div>
              ))}
            </div>
            </div>

            <div className="lg:col-span-2 lg:sticky lg:top-6 space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Report Preview</p>
                  </div>
                  <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">Example</span>
                </div>

                <div className="px-5 py-5">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <p className="text-sm font-bold text-slate-900">Sprint Retrospective Report</p>
                        <p className="text-xs text-slate-500">Executive summary • Themes • Action items</p>
                      </div>
                      <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
                        <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                    </div>

                    <div className="mt-4 space-y-3">
                      <div className="flex items-start gap-2.5">
                        <span className="mt-0.5 w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs flex items-center justify-center font-semibold flex-shrink-0">1</span>
                        <div className="flex-1">
                          <div className="h-2.5 w-3/5 bg-slate-200 rounded" />
                          <div className="mt-2 h-2.5 w-full bg-slate-200 rounded" />
                          <div className="mt-2 h-2.5 w-4/5 bg-slate-200 rounded" />
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-xs flex items-center justify-center font-semibold flex-shrink-0">2</span>
                        <div className="flex-1">
                          <div className="h-2.5 w-1/2 bg-slate-200 rounded" />
                          <div className="mt-2 h-2.5 w-11/12 bg-slate-200 rounded" />
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <span className="mt-0.5 w-5 h-5 rounded-full bg-orange-100 text-orange-700 text-xs flex items-center justify-center font-semibold flex-shrink-0">3</span>
                        <div className="flex-1">
                          <div className="h-2.5 w-2/3 bg-slate-200 rounded" />
                          <div className="mt-2 h-2.5 w-5/6 bg-slate-200 rounded" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 mt-3">
                    Paste your notes (or try sample data) to see a real preview instantly.
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Reduced cognitive load</p>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-start gap-2.5">
                    <span className="mt-0.5 w-4 h-4 rounded bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">✓</span>
                    <span>Structured input — no “what should I type?” guesswork.</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="mt-0.5 w-4 h-4 rounded bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">✓</span>
                    <span>Action items extracted automatically.</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="mt-0.5 w-4 h-4 rounded bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">✓</span>
                    <span>Fast feedback loop — run it in under a minute.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading */}
        {pageState === 'loading' && <LoadingCard />}

        {/* Report */}
        {(pageState === 'preview' || pageState === 'full') && report && (
          <>
            <div className="no-print mt-6 flex items-center justify-between">
              <button onClick={handleReset} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1.5 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Generate new report
              </button>
              {isFullView && (
                <button onClick={() => window.print()} className="flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PDF
                </button>
              )}
            </div>

            <ReportSection report={report} isFullView={isFullView} onUnlockClick={() => setShowAuthModal(true)} reportRef={reportRef} />

            {isFullView && (
              <div className="no-print mt-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white text-center">
                <h3 className="font-bold text-lg mb-1">Run structured retros with your whole team</h3>
                <p className="text-orange-100 text-sm mb-4">With Retrosive you get real boards, real-time collaboration, and AI reports on every sprint — automatically.</p>
                <Link to="/app/dashboard" className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold rounded-xl text-sm transition-colors">
                  Go to Dashboard
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
              </div>
            )}
          </>
        )}
      </div>

      <div className="no-print"><FooterSection /></div>

      {showAuthModal && <AuthModal onSuccess={handleAuthSuccess} onClose={() => setShowAuthModal(false)} />}
    </div>
  );
};

export default FreeAiReportPage;
